import React, { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { addBreadcrumb, logger, setTag } from '../utils/sentryUtils';

// Higher-order component for wrapping components with Sentry tracking
export const withSentryTracking = (WrappedComponent, componentName, options = {}) => {
  const SentryTrackedComponent = (props) => {
    const componentRef = useRef(null);
    const mountTime = useRef(Date.now());
    
    useEffect(() => {
      // Track component mount
      addBreadcrumb("Component mounted", "ui.component", { 
        componentName,
        mountTime: new Date().toISOString()
      });
      
      logger.debug("Component mounted", { 
        componentName,
        mountTime: new Date().toISOString(),
        props: Object.keys(props)
      });
      
      // Set component-specific tags
      setTag("component.name", componentName);
      setTag("component.type", "react");
      
      // Track component render performance
      const renderTime = Date.now() - mountTime.current;
      logger.debug("Component render time", { 
        componentName,
        renderTimeMs: renderTime
      });
      
      return () => {
        // Track component unmount
        const lifetime = Date.now() - mountTime.current;
        addBreadcrumb("Component unmounted", "ui.component", { 
          componentName,
          lifetimeMs: lifetime
        });
        
        logger.debug("Component unmounted", { 
          componentName,
          lifetimeMs: lifetime
        });
      };
    }, [componentName]);
    
    // Wrap the component with Sentry error boundary
    const SentryWrappedComponent = Sentry.withErrorBoundary(
      WrappedComponent,
      {
        fallback: ({ error, componentStack, resetError }) => (
          <div style={{ 
            padding: '20px', 
            textAlign: 'center',
            fontFamily: 'Arial, sans-serif',
            border: '1px solid #ff6b6b',
            borderRadius: '8px',
            backgroundColor: '#fff5f5'
          }}>
            <h3>Something went wrong in {componentName}</h3>
            <p>We've been notified and are working to fix this issue.</p>
            <button 
              onClick={resetError}
              style={{
                padding: '8px 16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                marginRight: '10px'
              }}
            >
              Try again
            </button>
            {process.env.NODE_ENV === 'development' && (
              <details style={{ marginTop: '15px', textAlign: 'left' }}>
                <summary>Error details (development only)</summary>
                <pre style={{ 
                  backgroundColor: '#f8f9fa', 
                  padding: '10px', 
                  borderRadius: '4px',
                  overflow: 'auto',
                  fontSize: '12px'
                }}>
                  {error.toString()}
                  {componentStack}
                </pre>
              </details>
            )}
          </div>
        ),
        onError: (error, componentStack) => {
          // Log the error with component context
          logger.error("Component error", {
            componentName,
            error: error.message,
            componentStack,
            props: Object.keys(props)
          });
        }
      }
    );
    
    // Wrap with performance monitoring
    const SentryProfiledComponent = Sentry.withProfiler(
      SentryWrappedComponent,
      {
        name: componentName,
        includeUpdates: options.includeUpdates !== false,
        includeRender: options.includeRender !== false,
      }
    );
    
    return (
      <div ref={componentRef}>
        <SentryProfiledComponent {...props} />
      </div>
    );
  };
  
  // Set display name for debugging
  SentryTrackedComponent.displayName = `withSentryTracking(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  
  return SentryTrackedComponent;
};

// Hook for component-level Sentry tracking
export const useSentryTracking = (componentName, options = {}) => {
  const componentRef = useRef(null);
  const mountTime = useRef(Date.now());
  
  useEffect(() => {
    // Track component mount
    addBreadcrumb("Component mounted", "ui.component", { 
      componentName,
      mountTime: new Date().toISOString()
    });
    
    logger.debug("Component mounted", { 
      componentName,
      mountTime: new Date().toISOString()
    });
    
    // Set component-specific tags
    setTag("component.name", componentName);
    setTag("component.type", "react");
    
    return () => {
      // Track component unmount
      const lifetime = Date.now() - mountTime.current;
      addBreadcrumb("Component unmounted", "ui.component", { 
        componentName,
        lifetimeMs: lifetime
      });
      
      logger.debug("Component unmounted", { 
        componentName,
        lifetimeMs: lifetime
      });
    };
  }, [componentName]);
  
  // Return ref for component tracking
  return componentRef;
};

// Default export for the HOC
export default withSentryTracking;
